import { MouseEvent, useEffect, useState } from 'react';
import { PATHS, navigate } from '@client/routes';
import { Box, Typography } from '@mui/material';
import classnames from 'classnames';
import { Post, ReactionType } from '../../../../generated/graphql';
import { PostHeader } from './PostHeader';
import { PostContent } from './PostContent';
import { PostReactions } from './PostReactions';
import { PostFooter } from './PostFooter';
import { noop } from 'lodash';
import { SensitiveContent } from './SensitiveContent';
import { trackEvent } from '@services/tracking';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbtack } from '@fortawesome/pro-solid-svg-icons';

type PostAuthor = {
  id: number;
  username: string;
  badges: string[];
  profilePicture: string;
  email: string;
  marketing: boolean;
};

type PostData = {
  id: string;
  text: string;
  createdAt: Date;
  author: PostAuthor;
  media: Post['media'];
  reactions: Post['reactions'];
  isSensitiveContent: boolean;
};

type CommunityPostCellProps = {
  influencerId: number;
  communityId: number;
  isUserCommunityOwner: boolean;
  postData: PostData;
  onAddComment?: () => void;
  onDelete?: () => void;
  onBlock?: (communityId: number, userId: number) => void;
  onToggleReaction?: (reactionType: ReactionType) => void;
  isPostPage?: boolean;
  onReplyInDM?: () => void;
  isPinned?: boolean;
  onPin?: () => void;
  pinLimitReached?: boolean;
  isHighlighted?: boolean;
};

export const CommunityPostCell = ({
  influencerId,
  communityId,
  isUserCommunityOwner,
  postData,
  onAddComment = undefined,
  onDelete = noop,
  onBlock = noop,
  onToggleReaction,
  isPostPage = false,
  onReplyInDM,
  isPinned,
  onPin,
  pinLimitReached,
  isHighlighted,
}: CommunityPostCellProps) => {
  const [hideSensitiveContent, setHideSensitiveContent] = useState<boolean>(
    postData.isSensitiveContent,
  );
  const handleOnAddComment = () => {
    trackEvent({
      type: 'Comment Added',
      payload: {
        'Post Type':
          postData.author.id === influencerId ? 'Creator' : 'Consumer',
        'Media Type': postData.media
          .map((m) => (m.video ? 'video' : 'image'))
          .join(', '),
      },
    });

    if (onAddComment) {
      onAddComment();
    } else {
      goToCommunityComments();
    }
  };

  const goToCommunityComments = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }
    navigate(PATHS.COMMUNITY_POST(influencerId, communityId, postData.id));
  };

  const handleHideSensitiveContent = (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    setHideSensitiveContent(false);
  };

  useEffect(() => {
    trackEvent({
      type: 'Post Viewed',
      payload: {
        'Has Comments': String(
          postData.reactions?.some((reaction) => reaction.type === 'Comment'),
        ),
        'Has Reactions': String(postData.reactions?.length > 0),
        'Media Count': postData.media.length,
        'Number of comments':
          postData.reactions?.filter((reaction) => reaction.type === 'Comment')
            .length || 0,
        'Number of reactions': postData.reactions?.length || 0,
        'Post Type':
          postData.author.id === influencerId ? 'Creator' : 'Consumer',
        'Media Type': postData.media
          .map((m) => (m.video ? 'video' : 'image'))
          .join(', '),
      },
    });
  }, []);

  return (
    <Box
      id={`post-${postData.id}`}
      className={classnames(
        'flex flex-col justify-start gap-4 p-4 transition-colors duration-500',
        {
          'bg-[#EDE5FC] rounded-[10px] my-2 mx-2': isPinned,
          'border-t first:border-0': !isPinned,
          'bg-purple-50': isHighlighted && !isPinned,
        },
      )}
      onClick={goToCommunityComments}
    >
      {isPinned && (
        <Box className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faThumbtack}
            className="text-[#8A8E99] text-[10px]"
          />
          <Typography
            className="text-[12px]"
            sx={{ color: 'rgba(0,0,0,0.5)', fontWeight: 500 }}
          >
            Pinned
          </Typography>
        </Box>
      )}
      <PostHeader
        communityId={communityId}
        author={postData.author}
        createdAt={postData.createdAt}
        id={postData.id}
        isUserCommunityOwner={isUserCommunityOwner}
        onDelete={onDelete}
        onBlock={onBlock}
        onReplyInDM={onReplyInDM}
        isPinned={isPinned}
        onPin={onPin}
        pinLimitReached={pinLimitReached}
      />
      {hideSensitiveContent ? (
        <SensitiveContent
          isPostCell={true}
          handleHideSensitiveContent={handleHideSensitiveContent}
        />
      ) : (
        <>
          <PostContent
            text={postData.text}
            media={postData.media}
            isPostPage={isPostPage}
            isPinned={isPinned}
          />
          <PostReactions
            reactions={postData.reactions || []}
            onToggleReaction={onToggleReaction}
          />
          <PostFooter
            reactions={postData.reactions || []}
            onAddComment={handleOnAddComment}
            hideAddComment={isPostPage}
          />
        </>
      )}
    </Box>
  );
};
